import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { BRAND } from "./config";

export const fieldProps = {
  fullWidth: true,
  size: "small",
};

export function EditorHeader({ title, description, badge }) {
  return (
    <Box sx={{ mb: 2.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.45 }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: "-.025em" }}>
          {title}
        </Typography>
        {badge ? (
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.3,
              borderRadius: 999,
              bgcolor: "rgba(243,90,58,.10)",
              color: BRAND.orange,
              fontSize: ".72rem",
              fontWeight: 900,
            }}
          >
            {badge}
          </Box>
        ) : null}
      </Stack>
      {description ? (
        <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)", lineHeight: 1.55 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
}

export function EditorPanel({ title, description, action, children, sx }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: "rgba(25,36,47,.10)",
        boxShadow: "0 8px 24px rgba(25,36,47,.035)",
        ...sx,
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {title || action ? (
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              {title ? <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{title}</Typography> : null}
              {description ? (
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25, lineHeight: 1.45 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
            {action}
          </Stack>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

export function SwitchRow({ label, helper, checked, onChange, disabled = false, dense = false }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{ py: dense ? 0.35 : 0.7, minHeight: dense ? 42 : 48 }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 850, lineHeight: 1.25 }}>{label}</Typography>
        {helper ? (
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.2, lineHeight: 1.35 }}>
            {helper}
          </Typography>
        ) : null}
      </Box>
      <Switch checked={Boolean(checked)} onChange={onChange} disabled={disabled} size="small" />
    </Stack>
  );
}

export function VisibilityPanel({ label, checked, onChange, disabled, helper }) {
  return (
    <EditorPanel sx={{ mb: 2, bgcolor: checked ? "rgba(243,90,58,.035)" : "rgba(25,36,47,.025)" }}>
      <SwitchRow
        label={`Mostrar ${label}`}
        helper={helper || (checked ? "Visible en la landing pública" : "Oculta, pero sigue disponible en el editor")}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
    </EditorPanel>
  );
}

export function AdvancedPanel({ title = "Opciones avanzadas", description, children, defaultExpanded = false }) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        border: "1px solid rgba(25,36,47,.10)",
        borderRadius: "12px !important",
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2, minHeight: 54 }}>
        <Box>
          <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          {description ? <Typography variant="caption" color="text.secondary">{description}</Typography> : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0.5, pb: 2 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}
